// The emitter behind the CLI's machine surface.
//
// One place decides what lands on stdout in machine mode, because stdout purity
// is the whole contract: a wrapper parsing a stream cannot recover from one
// stray `console.log`. Commands call this rather than writing JSON themselves,
// so the envelope, the clock and the stdout/stderr split are decided once.
//
// In `pretty` mode every method is a no-op on stdout and the command's own prose
// is unaffected — the machine surface is additive, not a second rendering path.

import {
  EVENT_SCHEMA_VERSION, levelViolatesInvariant,
  type EventCommand, type EventLevel, type EventStatus, type InstallEvent,
} from "@m8t-stack/install-contract";

export interface EmitterOptions {
  command: EventCommand;
  /** `json` streams events; `pretty` emits nothing and leaves prose alone. */
  mode: "pretty" | "json";
  /** Where events go. stdout in machine mode — nothing else may write there. */
  write: (line: string) => void;
  /** Absent only before an install exists (preflight). */
  installId?: string;
  /** Injectable for tests; defaults to the wall clock. */
  now?: () => Date;
}

export interface StepArgs {
  step: string;
  status: EventStatus;
  level?: EventLevel;
  code?: string;
  message?: string;
  installId?: string;
  data?: Record<string, unknown>;
}

/**
 * Emits install events as NDJSON, or nothing at all in pretty mode.
 *
 * `level` is derived rather than asked for, because the contract pins it for
 * every terminal status and a caller passing one by hand is a caller who can get
 * it wrong. It is accepted only where the contract leaves it free — a `running`
 * step — which is exactly the mid-step advisory case.
 */
export class InstallEventEmitter {
  private readonly opts: EmitterOptions;
  private installId: string | undefined;

  constructor(opts: EmitterOptions) {
    this.opts = opts;
    this.installId = opts.installId;
  }

  /** True when events are actually going somewhere. */
  get active(): boolean {
    return this.opts.mode === "json";
  }

  /**
   * Record the install id once it exists.
   *
   * `launch` mints it partway through its own run, so the emitter learns it
   * mid-stream rather than at construction. Every event after this carries it.
   */
  setInstallId(id: string): void {
    if (id === "") return;
    this.installId = id;
  }

  /** Emit one event. No-op in pretty mode. */
  emit(args: StepArgs): void {
    if (!this.active) return;
    const level = resolveLevel(args.status, args.level);
    const event: InstallEvent = {
      schemaVersion: EVENT_SCHEMA_VERSION,
      ts: (this.opts.now?.() ?? new Date()).toISOString(),
      command: this.opts.command,
      step: args.step,
      status: args.status,
      level,
      ...(args.code !== undefined ? { code: args.code } : {}),
      ...(args.message !== undefined ? { message: args.message } : {}),
      ...(idFor(args.installId ?? this.installId) ),
      ...(args.data !== undefined ? { data: args.data } : {}),
    };
    this.opts.write(JSON.stringify(event) + "\n");
  }

  /** A step began. */
  running(step: string, message?: string, data?: Record<string, unknown>): void {
    this.emit({ step, status: "running", ...(message !== undefined ? { message } : {}), ...(data !== undefined ? { data } : {}) });
  }

  /**
   * A mid-step advisory: the step is continuing, but something is worth saying.
   *
   * This is the one place `level` is genuinely free, and the surface the
   * installer's `warnings` array has always lacked — warnings are declared in
   * the status document and rendered nowhere, and they arrive here.
   */
  warn(step: string, message: string, data?: Record<string, unknown>): void {
    this.emit({ step, status: "running", level: "warn", message, ...(data !== undefined ? { data } : {}) });
  }

  /** A step finished successfully. */
  ok(step: string, message?: string, data?: Record<string, unknown>): void {
    this.emit({ step, status: "ok", ...(message !== undefined ? { message } : {}), ...(data !== undefined ? { data } : {}) });
  }

  /** A step was deliberately not run. */
  skipped(step: string, message?: string, data?: Record<string, unknown>): void {
    this.emit({ step, status: "skipped", ...(message !== undefined ? { message } : {}), ...(data !== undefined ? { data } : {}) });
  }

  /** A step failed. `code` reuses the CLI's existing error codes. */
  failed(step: string, code: string, message: string, data?: Record<string, unknown>): void {
    this.emit({ step, status: "failed", code, message, ...(data !== undefined ? { data } : {}) });
  }
}

function idFor(id: string | undefined): { installId?: string } {
  // An empty string satisfies "present" while joining to nothing — the exact
  // failure the id exists to prevent. Treated as absent.
  return id === undefined || id === "" ? {} : { installId: id };
}

/**
 * Run `body`, and make sure the stream ends on a terminal event whatever
 * happens.
 *
 * The contract promises a consumer that the last line of a stream is terminal.
 * A command that throws partway leaves the last line saying `running`, which a
 * wrapper cannot tell from an install still in progress — so it waits, or
 * reports success, on a run that is already over.
 */
export async function withTerminalEvent<T>(
  events: InstallEventEmitter,
  step: string,
  body: () => Promise<T>,
): Promise<T> {
  try {
    return await body();
  } catch (e) {
    const code = (e as { code?: unknown }).code;
    events.failed(step, typeof code === "string" ? code : "UNEXPECTED_ERROR", (e as Error).message);
    throw e;
  }
}

/**
 * The contract pins level for terminal statuses, so derive it there and accept a
 * caller's choice only while running. A caller that passes a level contradicting
 * a terminal status is not honoured quietly — the contract's own validator would
 * reject the result, so honouring it would emit an event we know to be invalid.
 */
export function resolveLevel(status: EventStatus, requested?: EventLevel): EventLevel {
  const pinned: EventLevel = status === "failed" ? "error" : "info";
  if (status === "running") return requested ?? "info";
  if (requested !== undefined && levelViolatesInvariant(status, requested) === null) return requested;
  return pinned;
}
